// Date formatting — SPEC-001 §13: every rendered date is MM/DD/YYYY, never a
// time, timezone, or month name. The bare string "Week 33" is a build failure
// (R-10) — callers needing a pay-period or ISO-week label must use
// formatPayPeriod/formatIsoWeek below, never assemble "Week N" by hand.

import { clinicToday, CLINIC_TIME_ZONE } from './clinicDay';

const MM_DD_YYYY = /^\d{2}\/\d{2}\/\d{4}$/;

// A bare date, or a date column a driver rendered as midnight UTC. It names a
// day already, so it is read as that day and never shifted by any time zone.
const PLAIN_DAY = /^(\d{4})-(\d{2})-(\d{2})(?:T0+(?::0+)+(?:\.0+)?Z?)?$/;

/**
 * Format a Date (or ISO-parseable string/number) as MM/DD/YYYY.
 * Never includes a time, a timezone offset/name, or a month name.
 */
export function formatDate(input: Date | string | number): string {
  // Eastern time everywhere (owner, 2026-10-16). A bare day is that day; a
  // timestamp (string or number) is read on the clinic's calendar, never the
  // viewer's. A Date object is a calendar day built from local parts
  // (parseDateOnly, clinicNow, new Date(y, m, d)) and prints as such — to print
  // an instant held in a Date, pass clinicNow(instant).
  const plain = typeof input === 'string' ? PLAIN_DAY.exec(input) : null;
  let mm: string;
  let dd: string;
  let yyyy: string;
  if (plain) {
    [, yyyy, mm, dd] = plain;
  } else if (input instanceof Date) {
    if (Number.isNaN(input.getTime())) {
      throw new Error(`formatDate: invalid date input ${String(input)}`);
    }
    mm = String(input.getMonth() + 1).padStart(2, '0');
    dd = String(input.getDate()).padStart(2, '0');
    yyyy = String(input.getFullYear());
  } else {
    const instant = new Date(input);
    if (Number.isNaN(instant.getTime())) {
      throw new Error(`formatDate: invalid date input ${String(input)}`);
    }
    [yyyy, mm, dd] = clinicToday(instant).split('-');
  }
  const out = `${mm}/${dd}/${yyyy}`;
  // Defensive self-check: refuse to ever return something that isn't the
  // exact shape the design law requires.
  if (!MM_DD_YYYY.test(out)) {
    throw new Error(`formatDate: produced a non-conforming string "${out}"`);
  }
  return out;
}

/**
 * Format a pay period per R-10: never the bare "Week 33" string.
 * Always "Pay period <period-id> (<start> – <end>)".
 */
export function formatPayPeriod(periodId: string, start: Date | string, end: Date | string): string {
  return `Pay period ${periodId} (${formatDate(start)} – ${formatDate(end)})`;
}

/** Format an ISO week label per R-10: "Week 2026-W33 (08/10/2026–08/16/2026)". */
export function formatIsoWeek(isoWeekLabel: string, start: Date | string, end: Date | string): string {
  if (!/^\d{4}-W\d{2}$/.test(isoWeekLabel)) {
    throw new Error(`formatIsoWeek: expected an ISO week like "2026-W33", got "${isoWeekLabel}"`);
  }
  return `Week ${isoWeekLabel} (${formatDate(start)}–${formatDate(end)})`;
}

/**
 * Parse a bare "YYYY-MM-DD" (no time component) as a LOCAL calendar day.
 *
 * Never hand a date-only string straight to `new Date(string)` when the
 * result will be DISPLAYED: the ISO-8601 date-only grammar parses that as
 * UTC midnight, and formatting it back out with local getters (as
 * `formatDate` does) prints the day BEFORE in any zone behind UTC — exactly
 * the trap a personal calendar's own all-day events would fall into, since
 * an all-day event already names a day with no time attached and must not
 * shift by the reader's offset.
 */
export function parseDateOnly(ymd: string): Date {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd);
  if (!m) throw new Error(`parseDateOnly: expected YYYY-MM-DD, got "${ymd}"`);
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

/**
 * Time-of-day only — "9:00 AM", never a date and never a timezone name — on
 * the CLINIC's clock (America/New_York), never the viewer's (owner ruling,
 * 2026-10-16: Eastern everywhere, including My Mail / My Calendar).
 */
export function formatTimeOfDay(input: Date | string | number): string {
  const d = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(d.getTime())) {
    throw new Error(`formatTimeOfDay: invalid date input ${String(input)}`);
  }
  return new Intl.DateTimeFormat('en-US', { timeZone: CLINIC_TIME_ZONE, hour: 'numeric', minute: '2-digit' }).format(d);
}

/** Date + time — "09/18/2026, 2:30 PM": `formatDate`'s date half plus
 * `formatTimeOfDay`'s time half, both on the clinic's clock. Used by My
 * Mail for a thread's received time. */
export function formatDateTime(input: Date | string | number): string {
  const d = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(d.getTime())) {
    throw new Error(`formatDateTime: invalid date input ${String(input)}`);
  }
  return `${formatDate(clinicToday(d))}, ${formatTimeOfDay(d)}`;
}
